/**
 * Validation result component: shows ticket status after scan/search.
 * Compact design to fit on screen without scrolling.
 */

import {
  IconCheck,
  IconX,
  IconAlertTriangle,
} from "../../../../components/Icons.tsx";
import { formatTimeBangkok } from "../../../../lib/bangkok-time.ts";
import type { TicketValidationResult } from "../../shared/types.ts";

export interface ValidationResultProps {
  result: TicketValidationResult;
  /** Callback to mark ticket as used */
  onMarkUsed?: () => void;
  /** Callback to scan another ticket */
  onScanAnother: () => void;
  /** Is marking as used */
  isProcessing?: boolean;
}

export function ValidationResult({
  result,
  onMarkUsed,
  onScanAnother,
  isProcessing = false,
}: ValidationResultProps) {
  const { ticket, isValid, alreadyUsedAt, event, customerName } = result;

  // Determine status type
  const isAlreadyUsed = !isValid && alreadyUsedAt !== null;

  // Status colors and icons
  const statusConfig = isValid
    ? {
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        iconBg: "bg-green-500",
        textColor: "text-green-800",
        Icon: IconCheck,
        title: "Действителен",
      }
    : isAlreadyUsed
      ? {
          bgColor: "bg-amber-50",
          borderColor: "border-amber-200",
          iconBg: "bg-amber-500",
          textColor: "text-amber-800",
          Icon: IconAlertTriangle,
          title: "Уже использован",
        }
      : {
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          iconBg: "bg-red-500",
          textColor: "text-red-800",
          Icon: IconX,
          title: "Не найден",
        };

  return (
    <div
      className={`rounded-2xl ${statusConfig.bgColor} border ${statusConfig.borderColor} p-4`}
    >
      {/* Status header with icon */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-12 h-12 rounded-full ${statusConfig.iconBg} flex items-center justify-center flex-shrink-0`}
        >
          <statusConfig.Icon className="w-6 h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={`text-lg font-bold ${statusConfig.textColor}`}>
            {statusConfig.title}
          </h2>
          {ticket && (
            <p className="text-[15px] text-slate-700 font-medium">
              Билет #{ticket.ticketNumber}
              {ticket.ticketType && (
                <span className="text-slate-500 font-normal">
                  {" "}
                  · {ticket.ticketType.name}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Compact info rows */}
      {ticket && (
        <div className="space-y-2 mb-4">
          {/* Customer */}
          {customerName && (
            <div className="flex items-center justify-between py-2 px-3 bg-white/60 rounded-lg">
              <span className="text-[13px] text-slate-500">Владелец</span>
              <span className="text-[14px] font-medium text-slate-900">
                {customerName}
              </span>
            </div>
          )}

          {/* Event */}
          {event && (
            <div className="flex items-center justify-between py-2 px-3 bg-white/60 rounded-lg">
              <span className="text-[13px] text-slate-500">Событие</span>
              <span className="text-[14px] font-medium text-slate-900 text-right truncate max-w-[60%]">
                {event.title}
              </span>
            </div>
          )}

          {/* Already used time */}
          {isAlreadyUsed && alreadyUsedAt && (
            <div className="flex items-center justify-between py-2 px-3 bg-amber-100/50 rounded-lg">
              <span className="text-[13px] text-amber-700">Использован в</span>
              <span className="text-[14px] font-medium text-amber-800">
                {formatTimeBangkok(alreadyUsedAt)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onScanAnother}
          className="flex-1 min-h-[46px] rounded-xl bg-white border border-slate-200 text-slate-700 text-[15px] font-medium active:bg-slate-50"
        >
          Ещё
        </button>
        {isValid && onMarkUsed && (
          <button
            type="button"
            onClick={onMarkUsed}
            disabled={isProcessing}
            className="flex-[2] min-h-[46px] rounded-xl bg-green-600 text-white text-[15px] font-medium active:opacity-90 disabled:opacity-50 flex items-center justify-center"
          >
            {isProcessing ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              "Отметить"
            )}
          </button>
        )}
      </div>
    </div>
  );
}
